/**
 * Canvas Pin / Pinned-Expanded — reusable primitive
 * (VISTOERA_CANVAS_PIN_EXPANDED_AND_GENERATE_VIDEO_STRUCTURED_ASSET_INPUT_PORTS_REFERENCE_VIDEO_FOUNDATION_P0_V1
 * §P1-§P5).
 *
 * "固定展开" — lets the user keep ONE (or several) high-frequency
 * generation cards expanded across `CanvasBoard`'s own Canvas-wide
 * single-`expandedNodeId` auto-collapse (blank-canvas click / Escape /
 * opening a different node all currently clear that one shared value —
 * see `CanvasBoard.tsx`'s `onPaneClick`/keydown handler/`onNodeClick`).
 * Pin does NOT replace that mechanism or introduce a second one; it is
 * OR'd into the same `isDetailExpanded` boolean each node already
 * receives (`CanvasBoard.tsx`: `isDetailExpanded: n.id === expandedNodeId
 * || isNodePinnedExpanded(data.fields)`), so a pinned node keeps
 * rendering expanded regardless of which node currently owns
 * `expandedNodeId`.
 *
 * PERSISTENCE (§P3) — presentation state, but stored in the SAME
 * `fields` JSON bag (`CanvasNode.data_json`) every other node field
 * already round-trips through via `patchFields`/`onChangeFields`. No
 * new table, no new top-level `CanvasNodeDto` column — refresh/hydrate
 * simply reloads `fields.pinnedExpanded` like any other field.
 *
 * PERFORMANCE (§P5) — one field write per toggle, nothing else. No
 * ResizeObserver, no rAF loop, no polling; `updateNodeInternals` is
 * NOT needed for the pin toggle itself (it changes zero handle
 * geometry) — only the pre-existing expand/collapse geometry effects
 * (already triggered by `showInspector`/`isDetailExpanded` changing)
 * fire, exactly as they would for a normal (unpinned) expand/collapse.
 */

export const PINNED_EXPANDED_FIELD = 'pinnedExpanded'

export function isNodePinnedExpanded(fields: Record<string, unknown> | undefined | null): boolean {
  return Boolean(fields && fields[PINNED_EXPANDED_FIELD] === true)
}

export interface UsePinnedExpandedNodeResult {
  pinned: boolean
  togglePin: () => void
}

/** One-line integration for any node component that already exposes a
 * `fields`/`patchFields(patch)` pair — every generation card does (see
 * `GenerateVideoNode.tsx`/`GenerateImageNode.tsx`'s own local
 * `patchFields`, itself wrapping `patchCanvasFieldsIfChanged` +
 * `nodeData.onChangeFields`). Intentionally this thin — Pin has no
 * node-type-specific behavior to abstract beyond "read one boolean,
 * flip one boolean" (§P4 "reusable capability", not a bigger
 * shared-state framework). */
export function usePinnedExpandedNode(
  fields: Record<string, unknown> | undefined,
  patchFields: (patch: Record<string, unknown>) => void,
): UsePinnedExpandedNodeResult {
  const pinned = isNodePinnedExpanded(fields)
  const togglePin = () => patchFields({ [PINNED_EXPANDED_FIELD]: !pinned })
  return { pinned, togglePin }
}
